import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import AppFooter from '@/components/footer/AppFooter.vue';
import GuideModuleLoader from '@/features/editorial/components/home/GuideModuleLoader.vue';
import ArticleTimeline from '@/features/editorial/components/home/timeline/ArticleTimeline.vue';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useRoute } from 'vue-router/composables';
import { useI18n } from '@/helpers/composables/useI18n';
import { ArticleClickedData } from '@/features/editorial/types/editorial-types';
import { ArticleType } from '@/@types/graphql-types';

import { useEditorialTracking } from '@/features/editorial/tracking';
import { TIMELINE_MAX_PAGES } from '@/features/editorial/composables/useArticleTimeline';


import { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	name: 'GuideHome',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const { activeDomain } = storeToRefs(useRoutingStore());
const route = useRoute();
const { t, tc } = useI18n();

// ArticleType -> clickGuideItemLink property mapping for tracking
const articleTypeMapping = {
	[ArticleType.ListArticle]: 'editorial-list',
	[ArticleType.NewsArticle]: 'news',
	[ArticleType.ReviewArticle]: 'review',
	[ArticleType.TitleDetailArticle]: 'title',
} as const;

const { trackEditorial } = useEditorialTracking();

const showFooter = ref(true);
const showModules = ref(Number.isNaN(+route.query?.page) || +route.query?.page <= 1);
watch(
	() => route.query.page,
	newPage => {
		if (!newPage || +newPage <= 1) {
			showModules.value = true;
		}
	}
);

const head = computed(() => {
	const page = +route.query?.page || 1;
	const isOnPage = !showModules.value;

	const title = isOnPage ? t('WEBAPP_GUIDE_META_TITLE_PAGED', { page }).toString() : tc('WEBAPP_GUIDE_META_TITLE');
	const description = isOnPage
		? t('WEBAPP_GUIDE_META_DESCRIPTION_PAGED', { page }).toString()
		: tc('WEBAPP_GUIDE_META_DESCRIPTION');

	const baseUrl = `https://${activeDomain.value}.${JW_CONFIG.DOMAIN}${route.path}`;
	const canonicalPath = page > 1 ? `${baseUrl}?page=${page}` : baseUrl;

	const links: Array<{ vmid?: string; rel: string; href: string }> = [
		{ vmid: 'canonical', rel: 'canonical', href: canonicalPath },
	];
	if (page > 1) links.push({ rel: 'prev', href: page === 2 ? baseUrl : `${baseUrl}?page=${page - 1}` });
	if (page < TIMELINE_MAX_PAGES) links.push({ rel: 'next', href: `${baseUrl}?page=${page + 1}` });

	return {
		title,
		link: links,
		meta: [
			{ vmid: 'description', name: 'description', content: description },
			{ vmid: 'og:title', property: 'og:title', content: title },
			{ vmid: 'og:description', property: 'og:description', content: description },
		],
	};
});

expose({ head });

function handleArticleClick(data?: ArticleClickedData) {
	if (data) {
		trackEditorial('clickGuideItemLink', {
			spec: {
				property: articleTypeMapping[data.articleType ?? ArticleType.ListArticle] ?? 'editorial-list',
				label: `https://guides.${JW_CONFIG.DOMAIN}${data.slug ?? ''}`,
				value: data.index,
				useLastRoute: true,
			},
			immediate: true,
		});
	}
}

function handleAuthorClick(id: string | undefined) {
	if (id) {
		trackEditorial('clickOnAuthor', {
			spec: { property: id, useLastRoute: true },
			immediate: true,
		});
	}
}

return { __sfc: true,activeDomain, route, t, tc, articleTypeMapping, trackEditorial, showFooter, showModules, head, handleArticleClick, handleAuthorClick, AppFooter, GuideModuleLoader, ArticleTimeline }
}

})